import { useCallback } from "react";

import { Button, Flex, Typography } from "antd";

import { useAPI } from "@neu/libui";

import styles from "./PatientStatusCounts.module.scss";

interface PatientStatusCountsResponse {
	All?: number;
	Pending?: number;
	"On-Service"?: number;
	"On-Hold"?: number;
}

interface PatientStatusCountsProps {
	filters?: Record<string, unknown>;
	onFilterChange?: (filters: Record<string, unknown>) => void;
}

const STATUS_BADGES = ["All", "Pending", "On-Service", "On-Hold"] as const;

type StatusBadge = (typeof STATUS_BADGES)[number];
type BadgeTone = "all" | "default" | "success" | "warning";

function statusBadgeTone(status: StatusBadge): BadgeTone {
	switch (status) {
		case "All":
			return "all";
		case "On-Service":
			return "success";
		case "On-Hold":
			return "warning";
		default:
			return "default";
	}
}

function resolveSelectedStatus(
	filters: Record<string, unknown> | undefined
): StatusBadge {
	const raw = filters?.status;
	if (typeof raw !== "string" || raw.length === 0) {
		return "All";
	}
	const colonIdx = raw.indexOf(":");
	const status = colonIdx === -1 ? raw : raw.slice(colonIdx + 1);
	if (
		status === "Pending" ||
		status === "On-Service" ||
		status === "On-Hold"
	) {
		return status;
	}
	return "All";
}

export const PatientStatusCounts = ({
	filters = {},
	onFilterChange,
}: PatientStatusCountsProps) => {
	const [{ data }] = useAPI<PatientStatusCountsResponse>(
		"/patient/status-counts",
		{
			staleTime: 30_000,
			refetchOnWindowFocus: true,
		}
	);

	const selectedStatus = resolveSelectedStatus(filters);

	const handleStatusClick = useCallback(
		(status: StatusBadge) => {
			if (!onFilterChange) {
				return;
			}
			const nextFilters = { ...filters };
			if (status === "All") {
				delete nextFilters.status;
			} else {
				nextFilters.status = `eq:${status}`;
			}
			onFilterChange(nextFilters);
		},
		[filters, onFilterChange]
	);

	return (
		<Flex align="center" gap={8} className={styles.root} wrap>
			{STATUS_BADGES.map((status) => {
				const count = data?.[status] ?? 0;
				const tone = statusBadgeTone(status);
				const isSelected = selectedStatus === status;
				return (
					<Button
						key={status}
						type="default"
						aria-pressed={isSelected}
						className={`${styles.badge} ${styles[tone]} ${isSelected ? styles.selected : ""}`}
						onClick={() => {
							handleStatusClick(status);
						}}
					>
						<Typography.Text className={styles.label}>
							{status}
						</Typography.Text>
						<Typography.Text className={styles.count}>
							{count}
						</Typography.Text>
					</Button>
				);
			})}
		</Flex>
	);
};
